import { useCallback, useState } from 'react';
import { Editor, Range, Transforms } from 'slate';

export function useMention(
  editor,
  mentionables = [],
  opts = {},
  addMentionCallback
) {
  const { trigger = '@', maxSuggestions = 10 } = opts;

  const [target, setTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [index, setIndex] = useState(0);

  // отфильтрованные values (по search), ограниченные maxSuggestions
  const values = (
    search
      ? mentionables.filter(m =>
          (m.value || m.name || '').toLowerCase().includes(search.toLowerCase())
        )
      : mentionables
  ).slice(0, maxSuggestions);

  const onChangeMention = useCallback(() => {
    const { selection } = editor;
    if (!selection || !Range.isCollapsed(selection)) {
      setTarget(null);
      setSearch('');
      return;
    }

    let curr = selection.anchor;
    let found = false;
    let startPoint = null;

    for (let i = 0; i < 200; i++) {
      const prev = Editor.before(editor, curr, { unit: 'character' });
      if (!prev) break;

      const r = Editor.range(editor, prev, curr);
      const ch = Editor.string(editor, r);
      if (!ch) break;

      // если встретили пробел — прекращаем (упоминание не может пересекать пробел)
      if (/\s/.test(ch)) break;

      // если это триггер — запоминаем точку старта
      if (ch === trigger) {
        found = true;
        startPoint = prev;
        break;
      }

      curr = prev;
    }

    if (!found || !startPoint) {
      setTarget(null);
      setSearch('');
      return;
    }

    const targetRange = Editor.range(editor, startPoint, selection.anchor);
    const token = Editor.string(editor, targetRange).slice(1); // убираем сам '@'
    setTarget(targetRange);
    setSearch(token);
    setIndex(0);
  }, [editor, trigger]);

  // keyboard navigation + enter/tab handling
  const onKeyDownMention = useCallback(
    e => {
      if (!target) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIndex(i => Math.min(i + 1, values.length - 1));
        return false;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIndex(i => Math.max(i - 1, 0));
        return false;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        // вставляем выбранный пункт
        e.preventDefault();
        const item = values[index];
        if (item) onAddMention(editor, item);
        return false;
      }
    },
    [target, search, values, index]
  );

  // вставка упоминания — заменяем range (target) на node mention и ставим курсор за ним
  const onAddMention = useCallback(
    (ed, item) => {
      if (!target) return;
      Transforms.select(ed, target);
      const mentionNode = {
        type: 'mention',
        // значение, которое плагин будет использовать
        value: item.name ?? item.value ?? '',
        data: item, // сохраняем всю инфу
        children: [{ text: '' }],
      };
      Transforms.insertNodes(ed, mentionNode);
      // вставим пробел после вставленного упоминания чтобы курсор был отделён
      Transforms.insertText(ed, ' ');
      // убираем подсказку
      setTarget(null);
      setSearch('');
      setIndex(0);

      addMentionCallback(item.data.id);
    },
    [target]
  );

  return {
    target,
    search,
    index,
    values,
    onChangeMention,
    onKeyDownMention,
    onAddMention,
  };
}
